import { observer } from 'mobx-react-lite';
import { Fragment } from 'react';

import { YStack } from '@/components/ui';
import { FEATURES, useFeature } from '@/features/feature-flags';
import {
  useHomeScheduleVisible,
  WidgetDisplayProvider,
  widgetRegistry,
} from '@/features/widgets';
import {
  widgetEntryKey,
  type RoutineIslandEntry,
  type SingletonEntry,
} from '@/features/widgets/widgets.types';
import { pluginsStore } from '@/plugins/plugins.store';

import {
  homePreferencesStore,
  type HomeSectionId,
} from './home-preferences.store';
import { HomeRoutinesSection } from './HomeRoutinesSection';
import { IntegrationSuggestionsSection } from './IntegrationSuggestionsSection';
import { ProactiveAgentFeed } from './ProactiveAgentFeed';

const HomeWidget = ({ entry }: { entry: SingletonEntry }) => {
  const WidgetComponent = widgetRegistry[entry.type];
  return <WidgetComponent />;
};

export const HomeSections = observer(() => {
  const hasFeature = useFeature();
  const scheduleVisible = useHomeScheduleVisible();
  const entries = homePreferencesStore.orderedVisibleEntries;

  const renderSection = (sectionId: HomeSectionId) => {
    if (!homePreferencesStore.isSectionVisible(sectionId)) return null;
    switch (sectionId) {
      case 'upcoming-meetings':
        if (!scheduleVisible) return null;
        return entries
          .filter(
            (entry): entry is SingletonEntry =>
              entry.kind === 'singleton' && entry.type === 'upcoming-meetings'
          )
          .map((entry) => (
            <HomeWidget key={widgetEntryKey(entry)} entry={entry} />
          ));
      case 'pinned-routines':
        return (
          <HomeRoutinesSection
            entries={entries.filter(
              (entry): entry is RoutineIslandEntry =>
                entry.kind === 'pinned-routine'
            )}
          />
        );
      case 'integration-suggestions':
        return <IntegrationSuggestionsSection />;
      case 'feed':
        if (!hasFeature(FEATURES.ACTION_CARDS_FEED)) return null;
        return <ProactiveAgentFeed />;
    }
  };

  // Every section is a direct child of one stack, so the gap between any two is the same
  return (
    <WidgetDisplayProvider configStore={homePreferencesStore} mode="home">
      <YStack className="gap-8 pt-6">
        {homePreferencesStore.sectionOrder.map((sectionId) => (
          <Fragment key={sectionId}>{renderSection(sectionId)}</Fragment>
        ))}
        {pluginsStore.homeSections.map(({ id, component: Section }) => (
          // `contents` keeps the section a direct child of the stack for its gap; `no-record` masks it in recordings
          <div key={id} className="no-record contents">
            <Section />
          </div>
        ))}
      </YStack>
    </WidgetDisplayProvider>
  );
});
